<template>
  <section :style="{ backgroundColor: props.backgroundColor }">
    <div class="px-4 py-10 mx-auto max-w-5xl md:py-14 xl:px-0">
      <h3
        class="text-3xl font-bold text-center lg:leading-10 xl:text-42 md:text-4xl font-ww-bold text-lifeboost-gray-lighter">
        {{ props.title }}
      </h3>

      <div class="mt-8 space-y-6 lg:mt-16 lg:mb-6">
        <div v-for="(item, i) in props.items" :key="i" class="p-5 bg-white rounded-lg lg:p-10">
          <button :id="`faq-${i}-btn`"
            class="flex justify-between items-center w-full text-left rounded-xl group focus:outline-none focus-visible:ring-2 focus-visible:ring-neutral-800"
            @click="toggle(i)" @keydown.enter.prevent="toggle(i)" @keydown.space.prevent="toggle(i)"
            :aria-expanded="isOpen(i)" :aria-controls="`faq-${i}-panel`">
            <span
              class="text-lg font-bold md:text-xl lg:text-21 md-max:leading-6 text-lifeboost-gray-lighter font-ww-medium"
              v-html="item.q"></span>



            <span class="inline-flex justify-center items-center ml-4 w-6 h-6" aria-hidden="true">

              <svg v-if="!isOpen(i)" width="19" height="19" viewBox="0 0 19 19" fill="none"
                xmlns="http://www.w3.org/2000/svg">
                <path d="M0.648438 9.55664H18.6211" stroke="black" stroke-width="2.24658" stroke-miterlimit="10" />
                <path d="M9.63281 0.570312V18.543" stroke="black" stroke-width="2.24658" stroke-miterlimit="10" />
              </svg>
              <svg v-else width="19" height="3" viewBox="0 0 19 3" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M0.648438 1.55664H18.6211" stroke="black" stroke-width="2.24658" stroke-miterlimit="10" />
              </svg>
            </span>
          </button>


          <transition @enter="onEnter" @after-enter="onAfterEnter" @leave="onLeave"
            enter-active-class="transition-all duration-500 ease-out"
            leave-active-class="transition-all duration-500 ease-in">
            <div v-show="isOpen(i)" :id="`faq-${i}-panel`"
              class="mt-3 text-base opacity-0 md:mt-6 font-ww-regular xl:text-21 md:text-lg text-lifeboost-gray-lighter xl:pr-20"
              role="region" :aria-labelledby="`faq-${i}-btn`" v-html="item.a">

            </div>
          </transition>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>

// Props for Builder/usage
const props = defineProps({
  title: {
    type: String,
    default: 'Frequently Asked Questions'
  },
  backgroundColor: {
    type: String,
    default: '#F2F2F2'
  },
  items: {
    type: Array,
    default: () => ([
      {
        q: 'How is my order prepared?',
        a: 'Each batch of coffee is roasted on order! Your coffee is so fresh that we actually recommend waiting about 7 days off roast to brew it so the coffee can properly degas. With that, the time it takes for the coffee to degas to reach optimal flavor coincides perfectly with the time it takes for the coffee to ship, so you\'re receiving the freshest coffee possible! Cheers!'
      },
      {
        q: 'Can I drink mushroom coffee if I have a medical condition?',
        a: 'If you have a medical condition—especially an autoimmune disorder, diabetes, or high/low blood pressure—we recommend consulting your healthcare provider before consuming mushroom coffee. Certain mushrooms can influence immune response, blood sugar levels, or blood pressure.'
      },
      {
        q: 'Will mushroom coffee interact with my medications?',
        a: 'Some mushrooms may interact with medications such as blood thinners, immunosuppressants, or diabetes medications. If you take prescription medications, speak with your healthcare provider before adding mushroom coffee to your routine.'
      },
      {
        q: 'Should the coffee have a vacuum seal?',
        a: 'You\'ll notice that our coffee isn\'t vacuum sealed. And the reason why is because you don\'t need it as we roast your coffee on order. A vacuum seal is only needed when buying coffee off the grocery store shelf where the coffee has been sitting for weeks if not months! Our bags that contain a one-way valve allowing the coffee to degas while minimizing oxidative degradation! As the coffee you order from us is roasted to order, you\'re getting the freshest coffee possible! And not coffee that has been roasted 30+ days ago!'
      },
      {
        q: 'What does Shade Grown mean?',
        a: 'Growing coffee plants under a sun filtering canopy improves the quality of the coffee bean for a number of reasons. The trees provide shelter for the coffee plants from the sun. This allows the beans to mature slower. This allows them to become more nutrient rich, denser, and harder than coffee beans that are not shade grown. This means that the final coffee product will be much more nutrient rich and have a much better flavor.\n\nThe trees that shade the coffee offer other benefits than just protection from the sun. The leaves that fall from the branches of the trees provide a naturally occurring source of nutrients for the coffee plants. When the leaves decompose they become part of the soil which the coffee plants use to grow. This helps to improve the nutrient density of the coffee plants. The fallen leaves also provide protection for the coffee plants from weed growth that act as a "mulch" making it hard for new seeds from other plants to grow. This means that there is more nutrients in the soil that are readily available for the coffee plants to use which will improve the nutrient richness of the coffee beans when they are fully developed.'
      },
      {
        q: 'Is your product tested for mycotoxins?',
        a: 'Yes, we send our beans off for 3rd party testing to test for mycotoxins and pesticides!'
      },
      {
        q: 'Is your coffee gluten-free?',
        a: 'Yes, our coffee is gluten free including our flavored coffee!'
      },
      {
        q: 'Where is your coffee produced?',
        a: 'Our coffee is grown in South America.'
      },
      {
        q: 'Is your coffee single origin?',
        a: 'Yes, all of our coffee comes from the same farm. Coffee being from a “single origin” means that all of the beans come from one area of one farm. This helps to ensure you that every bean that goes into your coffee is of the same high quality as all of the others. This also means that all of the beans are processed in the exact same way and roasted the same way. This keeps the other beans from mixing with others, which can affect the flavor of your coffee and introduce foreign things like mycotoxins that may be present in other coffee beans that could be blended in.'
      },
      {
        q: 'What does Mushroom coffee taste like?',
        a: 'Mushroom coffee tastes similar to regular coffee but with a slight earthy flavor due to the mushrooms. The taste of mushrooms is subtle and not overpowering.'
      },
      {
        q: 'Can I drink this coffee if I have a mushroom allergy?',
        a: 'Individuals with mushroom allergies should consult with a healthcare provider before consuming any products containing mushrooms.'
      },
      {
        q: 'Can you consume while pregnant or breastfeeding?',
        a: 'We always recommend consulting with your physician before use!'
      }
    ])
  }
})

// Currently open panel (single accordion)
const openIndex = ref(null)

const isOpen = (i) => openIndex.value === i
const toggle = (i) => {
  if (openIndex.value === i) {
    openIndex.value = null
  } else {
    openIndex.value = i
  }
}

// Smooth height and opacity animation
function onEnter(el) {
  el.style.height = '0px'
  el.style.overflow = 'hidden'
  el.style.opacity = '0'
  el.style.transition = 'height 500ms cubic-bezier(0.25, 0.46, 0.45, 0.94), opacity 400ms cubic-bezier(0.25, 0.46, 0.45, 0.94)'

  // Force reflow, then animate to real height
  requestAnimationFrame(() => {
    el.style.height = el.scrollHeight + 'px'
    el.style.opacity = '1'
  })
}

function onAfterEnter(el) {
  el.style.height = 'auto'
  el.style.overflow = 'visible'
  el.style.opacity = '1'
}

function onLeave(el) {
  el.style.height = el.scrollHeight + 'px'
  el.style.overflow = 'hidden'
  el.style.opacity = '1'
  el.style.transition = 'height 500ms cubic-bezier(0.25, 0.46, 0.45, 0.94), opacity 300ms cubic-bezier(0.25, 0.46, 0.45, 0.94)'

  // After one frame, animate to 0 for closing animation
  requestAnimationFrame(() => {
    el.style.height = '0px'
    el.style.opacity = '0'
  })
}

defineBuilderComponent({
  friendlyName: 'FAQ Accordion',
  inputs: [
    {
      name: 'title',
      type: 'text',
      defaultValue: 'Frequently Asked Questions'
    },
    {
      name: 'backgroundColor',
      type: 'color',
      defaultValue: '#F2F2F2'
    },
    {
      name: 'items',
      type: 'list',
      subFields: [
        { name: 'q', type: 'text', required: true },
        { name: 'a', type: 'richText', required: true }
      ],
      defaultValue: [
        {
          q: 'How is my order prepared?',
          a: 'Each batch of coffee is roasted on order! Your coffee is so fresh that we actually recommend waiting about 7 days off roast to brew it so the coffee can properly degas. With that, the time it takes for the coffee to degas to reach optimal flavor coincides perfectly with the time it takes for the coffee to ship, so you\'re receiving the freshest coffee possible! Cheers!'
        },
        {
          q: 'Can I drink mushroom coffee if I have a medical condition?',
          a: 'If you have a medical condition—especially an autoimmune disorder, diabetes, or high/low blood pressure—we recommend consulting your healthcare provider before consuming mushroom coffee. Certain mushrooms can influence immune response, blood sugar levels, or blood pressure.'
        },
        {
          q: 'Will mushroom coffee interact with my medications?',
          a: 'Some mushrooms may interact with medications such as blood thinners, immunosuppressants, or diabetes medications. If you take prescription medications, speak with your healthcare provider before adding mushroom coffee to your routine.'
        },
        {
          q: 'Should the coffee have a vacuum seal?',
          a: 'You\'ll notice that our coffee isn\'t vacuum sealed. And the reason why is because you don\'t need it as we roast your coffee on order. A vacuum seal is only needed when buying coffee off the grocery store shelf where the coffee has been sitting for weeks if not months! Our bags that contain a one-way valve allowing the coffee to degas while minimizing oxidative degradation! As the coffee you order from us is roasted to order, you\'re getting the freshest coffee possible! And not coffee that has been roasted 30+ days ago!'
        },
        {
          q: 'What does Shade Grown mean?',
          a: 'Growing coffee plants under a sun filtering canopy improves the quality of the coffee bean for a number of reasons. The trees provide shelter for the coffee plants from the sun. This allows the beans to mature slower. This allows them to become more nutrient rich, denser, and harder than coffee beans that are not shade grown. This means that the final coffee product will be much more nutrient rich and have a much better flavor.\n\nThe trees that shade the coffee offer other benefits than just protection from the sun. The leaves that fall from the branches of the trees provide a naturally occurring source of nutrients for the coffee plants. When the leaves decompose they become part of the soil which the coffee plants use to grow. This helps to improve the nutrient density of the coffee plants. The fallen leaves also provide protection for the coffee plants from weed growth that act as a "mulch" making it hard for new seeds from other plants to grow. This means that there is more nutrients in the soil that are readily available for the coffee plants to use which will improve the nutrient richness of the coffee beans when they are fully developed.'
        },
        {
          q: 'Is your product tested for mycotoxins?',
          a: 'Yes, we send our beans off for 3rd party testing to test for mycotoxins and pesticides!'
        },
        {
          q: 'Is your coffee gluten-free?',
          a: 'Yes, our coffee is gluten free including our flavored coffee!'
        },
        {
          q: 'Where is your coffee produced?',
          a: 'Our coffee is grown in South America.'
        },
        {
          q: 'Is your coffee single origin?',
          a: 'Yes, all of our coffee comes from the same farm. Coffee being from a "single origin" means that all of the beans come from one area of one farm. This helps to ensure you that every bean that goes into your coffee is of the same high quality as all of the others. This also means that all of the beans are processed in the exact same way and roasted the same way. This keeps the other beans from mixing with others, which can affect the flavor of your coffee and introduce foreign things like mycotoxins that may be present in other coffee beans that could be blended in.'
        },
        {
          q: 'What does Mushroom coffee taste like?',
          a: 'Mushroom coffee tastes similar to regular coffee but with a slight earthy flavor due to the mushrooms. The taste of mushrooms is subtle and not overpowering.'
        },
        {
          q: 'Can I drink this coffee if I have a mushroom allergy?',
          a: 'Individuals with mushroom allergies should consult with a healthcare provider before consuming any products containing mushrooms.'
        },
        {
          q: 'Can you consume while pregnant or breastfeeding?',
          a: 'We always recommend consulting with your physician before use!'
        }
      ]
    }
  ]
})
</script>